import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  IconButton,
  Grid,
  Card,
  CardContent,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  AccountBalance as AccountBalanceIcon,
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageSearchBar from '../components/PageSearchBar';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function fmtNum(n) {
  if (n == null) return '—';
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// Item class → color mapping
const CLASS_COLORS = {
  TV:  '#1565C0', SPK: '#2E7D32', CON: '#E65100', AMP: '#6A1B9A',
  SHD: '#00695C', LTG: '#F57F17', PRO: '#37474F', REC: '#C62828',
};
function classColor(cls) {
  return CLASS_COLORS[cls] || '#616161';
}

// Item class → human-readable description
const CLASS_DESCRIPTIONS = {
  ADP: 'Adapters/Splitters', AMP: 'Amplifiers', ANT: 'Antennas', ANZ: 'Analyzers',
  BTP: 'Battery/UPS', CAM: 'Cameras', CDP: 'CD Players', COM: 'Commercial Audio',
  CON: 'Connectivity/DM', CRD: 'Cards/Modules', DSS: 'DSS/Isolators', DVD: 'DVD/Blu-Ray',
  EIC: 'Electrical/Connectors', END: 'End Connectors', EQ: 'Equalizers', FED: 'Feedback Control',
  FUR: 'Furniture', GA: 'General/Notes', GTW: 'Gateways', HDW: 'Hardware/Back Boxes',
  HUB: 'Network/PoE', ICC: 'Optical/HDMI Cable', INT: 'Extenders', IRS: 'IR Control',
  LAB: 'Labor', LEC: 'Lecterns', LFT: 'Lifts/Mounts', LTG: 'Lighting/Keypads',
  MBR: 'Mounting/Brackets', MIC: 'Microphones', MIN: 'Mini Systems', MIS: 'Miscellaneous',
  MOD: 'Modules', NSP: 'Network/Small Parts', PAT: 'Patch/Plugs', PC: 'PCs/Modems',
  PLT: 'Plates/Buttons', POD: 'Tablets/iPads', POW: 'Power/Conditioners', PRE: 'Preamps',
  PRJ: 'Projectors', PRO: 'Processors', RAK: 'Racks/Accessories', RCR: 'Recorders/DVR',
  REC: 'Media Players', REL: 'Relays', REM: 'Remotes', SCL: 'Scalers/Converters',
  SCR: 'Screens', SHD: 'Shades/Window', SPK: 'Speakers', STD: 'Stands',
  SVR: 'Servers', SWI: 'Switches', SYS: 'Systems', TCR: 'Transceivers',
  TEL: 'Telecom', TNR: 'Tuners', TRN: 'Turntables', TV: 'TVs/Displays',
  VCR: 'VCR', WIR: 'Wire/Cable',
};

// ─── Summary Cards ────────────────────────────────────────────────────────────

const SummaryCard = ({ title, value, subtitle, icon, color }) => (
  <Card elevation={2}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15`, color }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

// ─── Expandable item-detail row ───────────────────────────────────────────────

const ItemDetailRow = ({ itemcode }) => {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`/api/warehouse/item/${encodeURIComponent(itemcode)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [itemcode]);

  if (loading) return <TableRow><TableCell colSpan={10}><LinearProgress /></TableCell></TableRow>;
  if (!detail)  return null;

  return (
    <TableRow sx={{ bgcolor: '#f0f4f8' }}>
      <TableCell colSpan={10} sx={{ py: 1.5 }}>
        <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ mb: 1, display: 'block' }}>
          {detail.master?.itemdescription} — All Locations
        </Typography>
        {detail.pricing && (
          <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">D-Tools Catalog Pricing</Typography>
            <Stack direction="row" spacing={3} alignItems="baseline" sx={{ mt: 0.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Sell Price</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D2C54' }}>
                  {fmtCurrency(detail.pricing.unit_price)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Unit Cost</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {fmtCurrency(detail.pricing.unit_cost)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Category</Typography>
                <Typography variant="body2">
                  {detail.pricing.category || '—'}{detail.pricing.subcategory ? ` / ${detail.pricing.subcategory}` : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Taxable</Typography>
                <Typography variant="body2">{detail.pricing.taxable == null ? '—' : (detail.pricing.taxable ? 'Yes' : 'No')}</Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  As of {fmtDate(detail.pricing.imported_at)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }} align="right">Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }} align="right">Value</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }} align="right">Avg Cost</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Last Issued</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Last Received</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.locations.map((loc) => (
              <TableRow key={loc.location} hover>
                <TableCell sx={{ py: 0.5 }}>
                  <Chip label={loc.location} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>{loc.description || '—'}</TableCell>
                <TableCell sx={{ py: 0.5 }} align="right">{fmtNum(loc.qtyonhand)}</TableCell>
                <TableCell sx={{ py: 0.5 }} align="right">{fmtCurrency(loc.valueonhand)}</TableCell>
                <TableCell sx={{ py: 0.5 }} align="right">{fmtCurrency(loc.avgcost)}</TableCell>
                <TableCell sx={{ py: 0.5 }}>{fmtDate(loc.lastissueddate)}</TableCell>
                <TableCell sx={{ py: 0.5 }}>{fmtDate(loc.lastreceiveddate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
};

// ─── Warehouse Inventory Tab ──────────────────────────────────────────────────

const WarehouseInventoryTab = ({ locations, selectedLocation, setSelectedLocation }) => {
  const navigate = useNavigate();
  const [search,           setSearch]           = useState('');
  const [searchInput,      setSearchInput]       = useState('');
  const [itemClass,        setItemClass]         = useState('');
  const [classes,          setClasses]           = useState([]);
  const [manufacturer,     setManufacturer]      = useState('');
  const [mfgOptions,       setMfgOptions]        = useState([]);
  const [items,            setItems]             = useState([]);
  const [total,            setTotal]             = useState(0);
  const [page,             setPage]              = useState(1);
  const [loading,          setLoading]           = useState(false);
  const [sortBy,           setSortBy]            = useState('valueonhand');
  const [sortDir,          setSortDir]           = useState('desc');
  const [expandedItem,     setExpandedItem]      = useState(null);
  const [userRole,         setUserRole]          = useState('');
  const [pricingMap,       setPricingMap]        = useState({}); // itemcode (upper) -> dtools pricing row
  const [catalogStatus,    setCatalogStatus]     = useState(null); // { latest_import, catalog }
  const LIMIT = 50;

  // Read user role from JWT to gate the 'Edit Mfg Swap Table' button
  useEffect(() => {
    try {
      const tok = localStorage.getItem('token');
      if (tok) {
        const payload = JSON.parse(atob(tok.split('.')[1]));
        setUserRole(payload.role || '');
      }
    } catch {}
  }, []);

  // Load classes + mfg options once
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/warehouse/classes', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setClasses(r.data || []))
      .catch(() => {});
    axios.get('/api/warehouse/mfg-swap?mapped=true', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const seen = new Set();
        const opts = [];
        (r.data?.rows || []).forEach(row => {
          const name = row.dtools_mfg || row.sage_mfg;
          if (name && !seen.has(name)) { seen.add(name); opts.push(name); }
        });
        opts.sort((a, b) => a.localeCompare(b));
        setMfgOptions(opts);
      })
      .catch(() => {});
  }, []);

  const fetchItems = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = { location: selectedLocation, page, limit: LIMIT };
    if (search)        params.search       = search;
    if (itemClass)     params.class        = itemClass;
    if (manufacturer)  params.manufacturer = manufacturer;
    axios.get('/api/warehouse/items', { headers: { Authorization: `Bearer ${token}` }, params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [selectedLocation, search, itemClass, manufacturer, page]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Load catalog status (shown as 'as of' badge in header)
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/warehouse/pricing-import-status', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCatalogStatus(r.data))
      .catch(() => setCatalogStatus(null));
  }, []);

  // Whenever items change, bulk-lookup D-Tools pricing for the visible page.
  useEffect(() => {
    if (!items.length) { setPricingMap({}); return; }
    const token = localStorage.getItem('token');
    const codes = Array.from(new Set(items.map(i => i.itemcode).filter(Boolean))).slice(0, 500);
    if (!codes.length) { setPricingMap({}); return; }
    axios.post('/api/warehouse/pricing-bulk',
      { itemcodes: codes },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => setPricingMap(r.data?.results || {}))
      .catch(() => setPricingMap({}));
  }, [items]);

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      setSearch(searchInput);
      setPage(1);
    }
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  // Client-side sort (items already fetched for this page).
  // D-Tools pricing lives in pricingMap keyed by uppercased itemcode, so pull
  // from there when sorting by the D-Tools sell price column.
  const getSortVal = (item) => {
    if (sortBy === 'dtools_unit_price') {
      const p = pricingMap[String(item.itemcode).toUpperCase()];
      return p?.unit_price != null ? parseFloat(p.unit_price) : null;
    }
    return item[sortBy];
  };
  const sortedItems = [...items].sort((a, b) => {
    const av = getSortVal(a), bv = getSortVal(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;  // nulls last, regardless of direction
    if (bv == null) return -1;
    const cmp = typeof av === 'string' ? av.localeCompare(bv) : parseFloat(av) - parseFloat(bv);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const isAllLocations = selectedLocation === 'ALL';
  const currentLoc = locations.find(l => l.location === selectedLocation);
  const allLocationsTotal = locations.reduce((s, l) => s + (parseFloat(l.total_value) || 0), 0);
  const allLocationsItems = locations.reduce((s, l) => s + (parseInt(l.item_count) || 0), 0);

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <PageSearchBar
          placeholder="Search item code or description…"
          value={searchInput}
          onChange={(v) => setSearchInput(v)}
          onKeyDown={handleSearchSubmit}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={selectedLocation}
            label="Location"
            onChange={(e) => { setSelectedLocation(e.target.value); setPage(1); }}
          >
            <MenuItem value="ALL">
              <strong>🔍 All Locations</strong>
            </MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.location} value={l.location}>
                {l.location}{l.description ? ` — ${l.description}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Class</InputLabel>
          <Select
            value={itemClass}
            label="Class"
            onChange={(e) => { setItemClass(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All Classes</MenuItem>
            {classes.map((c) => (
              <MenuItem key={c} value={c}>
                <Chip
                  label={c}
                  size="small"
                  sx={{ bgcolor: `${classColor(c)}20`, color: classColor(c), fontSize: '0.7rem', mr: 1 }}
                />
                {CLASS_DESCRIPTIONS[c] ? `${c} (${CLASS_DESCRIPTIONS[c]})` : c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          freeSolo
          options={mfgOptions}
          value={manufacturer}
          onChange={(_, v) => { setManufacturer(v || ''); setPage(1); }}
          onInputChange={(_, v, reason) => { if (reason === 'clear') { setManufacturer(''); setPage(1); } }}
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Manufacturer" />}
        />

        {(['Management', 'Purchasing', 'Warehouse'].includes(userRole)) && (
          <Tooltip title="Edit Sage→D-Tools manufacturer translation table">
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={() => navigate('/warehouse/mfg-swap')}
            >
              Mfg Swap
            </Button>
          </Tooltip>
        )}

        {(['Management', 'Purchasing', 'Warehouse', 'Finance'].includes(userRole)) && (
          <Tooltip title="Upload a new D-Tools catalog CSV to refresh sell prices">
            <Button
              size="small"
              variant="outlined"
              startIcon={<LocalOfferIcon />}
              onClick={() => navigate('/warehouse/pricing-upload')}
            >
              D-Tools Pricing
            </Button>
          </Tooltip>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {isAllLocations
            ? `${fmtCurrency(allLocationsTotal)} total • ${fmtNum(allLocationsItems)} items across ${locations.length} locations`
            : (currentLoc ? `${fmtCurrency(currentLoc.total_value)} total • ${fmtNum(currentLoc.item_count)} items` : '')}
        </Typography>
      </Box>

      {catalogStatus?.latest_import && (
        <Box sx={{ mb: 1 }}>
          <Chip
            size="small"
            icon={<LocalOfferIcon />}
            label={`D-Tools sell prices as of ${fmtDate(catalogStatus.latest_import.imported_at)}`}
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.72rem' }}
          />
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', width: 32 }} />
              {[
                { key: 'itemcode',         label: 'Item Code' },
                { key: 'itemdescription',  label: 'Description' },
                { key: 'manufacturer',     label: 'Manufacturer' },
                { key: 'class',            label: 'Class' },
                ...(isAllLocations ? [{ key: 'location', label: 'Location' }] : []),
                { key: 'qtyonhand',        label: 'Qty On Hand', align: 'right' },
                { key: 'valueonhand',      label: 'Value',        align: 'right' },
                { key: 'avgcost',          label: 'Avg Cost',     align: 'right' },
                { key: 'dtools_unit_price', label: 'D-Tools Sell Price', align: 'right' },
                { key: 'stockinguom',      label: 'UOM' },
                { key: 'lastissueddate',   label: 'Last Issued' },
                { key: 'lastreceiveddate', label: 'Last Received' },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{ fontWeight: 'bold' }}
                >
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={isAllLocations ? 13 : 12} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No items found</Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedItems.map((item) => (
              <React.Fragment key={`${item.location || 'x'}-${item.itemcode}`}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer', bgcolor: expandedItem === `${item.location}-${item.itemcode}` ? '#e8f0fe' : undefined }}
                  onClick={() => setExpandedItem(expandedItem === `${item.location}-${item.itemcode}` ? null : `${item.location}-${item.itemcode}`)}
                >
                  <TableCell sx={{ py: 0.5, width: 32 }}>
                    <IconButton size="small">
                      {expandedItem === `${item.location}-${item.itemcode}` ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                      {item.itemcode}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="body2">{item.itemdescription}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {item.manufacturer ? (
                      item.manufacturer_translated ? (
                        <Tooltip
                          title={`Sage value: ${item.manufacturer_sage}  →  D-Tools: ${item.manufacturer}`}
                          placement="top"
                        >
                          <Typography variant="body2" sx={{ borderBottom: '1px dotted #aaa', cursor: 'help', display: 'inline-block' }}>
                            {item.manufacturer}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">{item.manufacturer}</Typography>
                      )
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {item.class && (
                      <Chip
                        label={item.class}
                        size="small"
                        sx={{ bgcolor: `${classColor(item.class)}20`, color: classColor(item.class), fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  {isAllLocations && (
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip
                        label={item.location}
                        size="small"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600 }}
                        title={item.location_description || ''}
                      />
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ py: 0.5 }}>{fmtNum(item.qtyonhand)}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">{fmtCurrency(item.valueonhand)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>{fmtCurrency(item.avgcost)}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {(() => {
                      const p = pricingMap[String(item.itemcode).toUpperCase()];
                      if (!p) return <Typography variant="caption" color="text.disabled">—</Typography>;
                      const tipLines = [
                        `D-Tools model: ${p.model}`,
                        `Mfg: ${p.manufacturer}`,
                        p.category && `Category: ${p.category}${p.subcategory ? ' / ' + p.subcategory : ''}`,
                        p.unit_cost != null && `Unit cost: ${fmtCurrency(p.unit_cost)}`,
                        p.taxable != null && `Taxable: ${p.taxable ? 'Yes' : 'No'}`,
                        p.match_type && `Matched by: ${p.match_type.replace(/_/g, ' ')}`,
                      ].filter(Boolean).join('\n');
                      return (
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tipLines}</span>} placement="top" arrow>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#0D2C54', cursor: 'help', borderBottom: '1px dotted #aaa', display: 'inline-block' }}>
                            {fmtCurrency(p.unit_price)}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">{item.stockinguom || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">{fmtDate(item.lastissueddate)}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">{fmtDate(item.lastreceiveddate)}</Typography>
                  </TableCell>
                </TableRow>
                {expandedItem === `${item.location}-${item.itemcode}` && (
                  <ItemDetailRow itemcode={item.itemcode} />
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {total > LIMIT && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(total / LIMIT)}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Bin Detail Dialog ───────────────────────────────────────────────────────
// Clicking a row in the Project Bins tab opens this dialog showing every
// SKU currently in that bin (with qty + value). Tom Clancy 2026-05-14:
// previously the bin row navigated to /projects?search=..., which sent you
// to WIP instead of showing the bin contents. Fixed by giving bins their
// own focused view.

const BinDetailDialog = ({ bin, open, onClose }) => {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!open || !bin) return;
    const token = localStorage.getItem('token');
    setLoading(true);
    setError(null);
    axios
      .get('/api/warehouse/items', {
        params: { location: bin.location, page: 1, limit: 500 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setItems(r.data?.items || r.data || []))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [open, bin]);

  if (!bin) return null;

  const totalQty   = items.reduce((s, i) => s + (parseFloat(i.qtyonhand)   || 0), 0);
  const totalValue = items.reduce((s, i) => s + (parseFloat(i.valueonhand) || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip label={bin.location} size="small" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
              {bin.job_number}{bin.job_description ? ` — ${bin.job_description}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fmtNum(items.length)} SKUs · {fmtNum(totalQty)} qty · {fmtCurrency(totalValue)}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No items currently in this bin.
          </Typography>
        )}
        {!loading && items.length > 0 && (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Manufacturer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Class</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Value</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Avg Cost</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>UOM</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Issued</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Received</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={`${item.location}-${item.itemcode}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                        {item.itemcode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.itemdescription}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.manufacturer || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.class ? (
                        <Chip
                          label={item.class}
                          size="small"
                          sx={{ bgcolor: `${classColor(item.class)}20`, color: classColor(item.class), fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">{fmtNum(item.qtyonhand)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{fmtCurrency(item.valueonhand)}</Typography>
                    </TableCell>
                    <TableCell align="right">{fmtCurrency(item.avgcost)}</TableCell>
                    <TableCell><Typography variant="caption">{item.stockinguom || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(item.lastissueddate)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(item.lastreceiveddate)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Project Bins Tab ─────────────────────────────────────────────────────────

const ProjectBinsTab = () => {
  const [bins,    setBins]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [sortBy,  setSortBy]  = useState('total_value');
  const [sortDir, setSortDir] = useState('desc');
  // Bin click opens a dialog showing the items in that bin (was: navigated
  // to /projects?search=..., which sent the user to WIP). Tom Clancy 2026-05-14.
  const [selectedBin, setSelectedBin] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/warehouse/project-bins', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBins(r.data || []))
      .catch(() => setBins([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const filtered = bins.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (b.location        || '').toLowerCase().includes(q) ||
      (b.job_number      || '').toLowerCase().includes(q) ||
      (b.job_description || '').toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'string' ? av.localeCompare(bv) : parseFloat(av) - parseFloat(bv);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  if (loading) return <LinearProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <PageSearchBar
          placeholder="Search by project name or job number…"
          value={search}
          onChange={(v) => setSearch(v)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {sorted.length} bins • {fmtCurrency(sorted.reduce((s, b) => s + (parseFloat(b.total_value) || 0), 0))} total
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              {[
                { key: 'location',        label: 'Bin' },
                { key: 'job_number',      label: 'Job #' },
                { key: 'job_description', label: 'Project Name' },
                { key: 'item_count',      label: 'Items',      align: 'right' },
                { key: 'total_qty',       label: 'Total Qty',  align: 'right' },
                { key: 'total_value',     label: 'Total Value', align: 'right' },
              ].map((col) => (
                <TableCell key={col.key} align={col.align || 'left'} sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No project bins found</Typography>
                </TableCell>
              </TableRow>
            )}
            {sorted.map((bin) => (
              <TableRow
                key={bin.location}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => setSelectedBin(bin)}
              >
                <TableCell>
                  <Chip label={bin.location} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                    {bin.job_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{bin.job_description || '—'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{fmtNum(bin.item_count)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{fmtNum(bin.total_qty)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold" color={parseFloat(bin.total_value) > 10000 ? 'success.main' : 'text.primary'}>
                    {fmtCurrency(bin.total_value)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <BinDetailDialog
        bin={selectedBin}
        open={Boolean(selectedBin)}
        onClose={() => setSelectedBin(null)}
      />
    </Box>
  );
};

// ─── Main Warehouse Page ──────────────────────────────────────────────────────

const Warehouse = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  // Default to ALL locations — PMs/AfterCare often need to search across
  // every warehouse before narrowing. Tom Clancy 2026-05-14.
  const [selectedLocation, setSelectedLocation] = useState('ALL');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/warehouse/summary', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSummary(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>Warehouse & Inventory</Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
          Live inventory data from Sage 300
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error   && <Alert severity="warning" sx={{ mb: 2 }}>Could not connect to Sage: {error}</Alert>}

      {/* Summary Cards */}
      {summary && (() => {
        const currentLoc = (summary.locations || []).find(l => l.location === selectedLocation);
        const showLocValue = currentLoc ? parseFloat(currentLoc.total_value) || 0 : summary.warehouse_total_value;
        const showLocItems = currentLoc ? parseInt(currentLoc.item_count) || 0 : summary.warehouse_total_items;
        const showLocLabel = currentLoc ? currentLoc.location + (currentLoc.description ? ` — ${currentLoc.description}` : '') : 'All Locations';
        return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title={showLocLabel}
              value={fmtCurrency(showLocValue)}
              subtitle={`${fmtNum(showLocItems)} items`}
              icon={<WarehouseIcon />}
              color="#1A5490"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="All Warehouse Locations"
              value={fmtCurrency(summary.warehouse_total_value)}
              subtitle={`${summary.warehouse_location_count} locations • ${fmtNum(summary.warehouse_total_items)} SKUs`}
              icon={<InventoryIcon />}
              color="#2E7D32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Project Bin Value"
              value={fmtCurrency(summary.bin_total_value)}
              subtitle={`${summary.bin_count} active bins`}
              icon={<InventoryIcon />}
              color="#E65100"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Grand Total"
              value={fmtCurrency(summary.grand_total_value)}
              subtitle="all locations combined"
              icon={<AccountBalanceIcon />}
              color="#37474F"
            />
          </Grid>
        </Grid>
        );
      })()}

      {/* Tabs */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Warehouse Inventory" icon={<WarehouseIcon />} iconPosition="start" />
          <Tab label="Project Bins" icon={<InventoryIcon />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && summary && (
          <WarehouseInventoryTab locations={summary.locations || []} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} />
        )}
        {activeTab === 1 && (
          <ProjectBinsTab />
        )}
      </Paper>
    </Box>
  );
};

export default Warehouse;
